import { useState } from "react";
import { Button } from "../../Components/UI/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../Components/UI/dropdown-menu';
import { ChevronDown, Plus, ArrowLeft, Search, X, ListCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../Components/UI/dialog';
import { useSelector } from 'react-redux';
import { getActiveThreadTypesArr } from '../../Store/Reducers';
import ThreadSahiIcon from '../../Components/Common/ThreadSahiIcon';
import { checkUserRights, currentUserHasPermission, isSalesOnlyActive } from '../../Utils/Utilities';
import MobileFullScreenDropdown from '../../Components/Common/MobileDropdown';
import useIsMobile from '../../Hooks/useIsMobile';

const ThreadDropdown = ({
  className = "",
  onThreadTypeSelect,
  iconButton = true,
  connectionType = null,
  externalOpen = false,
  onExternalOpenChange = null,
  minimalIcon = false, // New prop for minimal plus icon style
  dropdownAlign = "end", // New prop to control dropdown alignment: "start" (left), "end" (right)
  title = "Enter Thread",
  activeFilteredTypes = null,
  isSmallBtn = false,
  threadTypeIDs = [],
  tskInqOnly = false,
  excludeThreadTypeIDs = [], // New prop: Array of thread type IDs to exclude
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isMobile = useIsMobile();

  const threadTypesAll = useSelector(state => state.masterData.threadTypes);
  const activeThreadTypes = useSelector(getActiveThreadTypesArr);

  let threadTypesData = threadTypeIDs.length ? threadTypesAll : activeThreadTypes;

  if (threadTypeIDs.length) {
    threadTypesData = threadTypesData.filter(type => threadTypeIDs.includes(type?.TID));
  }

  // Check if user can create threads (ThrActions 1 or 3)
  const thrActions = currentUserHasPermission('ThrActions');
  const canCreateThread = thrActions <= 3 && thrActions > 0;

  // Get user rights for customer and supplier access
  const userRights = checkUserRights();

  // Return null if user doesn't have permission to create threads
  if (!canCreateThread) {
    return null;
  }
  // Use external control if provided, otherwise use internal state
  const isOpen = onExternalOpenChange ? externalOpen : internalOpen;
  const setIsOpen = onExternalOpenChange ? onExternalOpenChange : setInternalOpen;

  // Filter thread types based on connection type
  const getFilteredThreadTypes = (threadTypes, connType) => {
    if (!connType) return threadTypes;

    const connectionTypeMapping = {
      'C': [1, 2, 3],
      'S': [4, 5, 6],
      'B': null
    };

    const allowedTypes = connectionTypeMapping[connType];
    return !allowedTypes ? threadTypes : threadTypes.filter(t => allowedTypes.includes(t.TID));
  };

  const baseThreadTypes = threadTypesData && Object.keys(threadTypesData).length > 0
    ? Object.values(threadTypesData).map(threadType => ({
      TID: threadType.TID,
      Typ: threadType.TID === 2 ? 'Sale' : threadType.TypSng || threadType.Typ,
      SnSng: threadType.TID === 2 ? 'Sale' : threadType.SnSng
    }))
    : [
      { TID: 1, Typ: 'Inquiry', SnSng: 'Inquiry' },
      { TID: 2, Typ: 'Sale', SnSng: 'Sale' },
      { TID: 3, Typ: 'Support', SnSng: 'Support' },
      { TID: 4, Typ: 'Purchase', SnSng: 'Purchase' },
      { TID: 5, Typ: 'Payment', SnSng: 'Payment' },
      { TID: 6, Typ: 'Service', SnSng: 'Service' }
    ];

  // Filter based on connectionType first
  var allThreadTypes = baseThreadTypes
  const salesOnlyMode = isSalesOnlyActive(threadTypesData);

  // Filter thread types based on user rights
  const getThreadTypesByUserRights = (threadTypes) => {
    const customerThreadTypes = [1, 2, 3]; // Inquiry, Order, Support
    const supplierThreadTypes = [4, 5, 6]; // Purchase, Payment, Service

    let allowedTypes = [];

    if (userRights.canSeeCstrs) {
      allowedTypes = [...allowedTypes, ...customerThreadTypes];
    }

    if (userRights.canSeeSplrs) {
      allowedTypes = [...allowedTypes, ...supplierThreadTypes];
    }

    return threadTypes.filter(t => allowedTypes.includes(t.TID));
  };

  const userFilteredThreadTypes = getThreadTypesByUserRights(allThreadTypes);
  let filteredThreadTypes = getFilteredThreadTypes(userFilteredThreadTypes, connectionType);

  if (activeFilteredTypes && activeFilteredTypes !== "123456") {
    const activeTypeIDs = activeFilteredTypes.split('').map(id => parseInt(id));
    filteredThreadTypes = filteredThreadTypes.filter(t => activeTypeIDs.includes(t.TID));
  }

  if (excludeThreadTypeIDs.length > 0) {
    filteredThreadTypes = filteredThreadTypes.filter(t => !excludeThreadTypeIDs.includes(t.TID));
  }

  if (tskInqOnly) {
    let finalThreadTypes = [];
    const inquiry = filteredThreadTypes.find(t => t.TID === 1);

    if (inquiry) {
      finalThreadTypes.push({
        ...inquiry,
        Typ: `Add ${inquiry.Typ}`
      });
    }

    finalThreadTypes.push({
      TID: -1,
      Typ: 'Add Task',
      SnSng: 'Add Task',
    });

    filteredThreadTypes = [...finalThreadTypes];
  }

  if (salesOnlyMode && !tskInqOnly) {
    if (filteredThreadTypes.length === 0) {
      return minimalIcon ? (
        <Plus className={`h-6 w-6 text-gray-400 cursor-not-allowed ${className}`} />
      ) : (
        <Button className={`${className} flex items-center`} disabled>
          <Plus className="h-4 w-4" />
          {title}
        </Button>
      );
    }

    if (isMobile && minimalIcon) {
      return (
        <MobileFullScreenDropdown
          options={filteredThreadTypes}
          value={null}
          onSelect={(selectedTID, selectedOption) => {
            onThreadTypeSelect(selectedTID);
          }}
          placeholder={`Select ${tskInqOnly ? '' : 'Thread'} Type`}
          displayKey="Typ"
          valueKey="TID"
          className={className}
          label="Thread Type"
          emptyMessage="No thread types available"
          searchThreshold={999}
          searchPlaceholder="Search thread types..."
          isIcon={true}
          showButtonStyle={false}
          buttonTitle={title}
          showClearButton={false}
          showAllOption={false}
          isSorting={false}
          mobileDropdownText={`Select ${tskInqOnly ? '' : 'Thread'} Type`}
          renderOption={(option, isSelected) => (
            <div className="flex items-center gap-3 w-full">
              {option.TID == -1 ? <ListCheck className="text-sahi-dark-blue" /> : <ThreadSahiIcon tID={option.TID} />}
              <span className="font-medium text-gray-900">
                {option.Typ}
              </span>
            </div>
          )}
        />
      );
    }

    return (
      <div className={`${className} ${isMobile ? 'mb-10' : ''}`}>
        <div className={`${isMobile
          ? 'flex flex-wrap gap-2 justify-start'
          : 'flex items-center gap-2'
          }`}>
          {filteredThreadTypes.map((threadType) => {
            const buttonText = `Enter ${threadType.SnSng}`;

            return (
              <Button
                key={threadType.TID}
                className={`
                  button-config sahi-primary-button flex items-center justify-center py-[6px] px-[10px]
                  ${!isSmallBtn && 'text-base'}
                  ${isMobile
                    ? 'w-fit min-h-[44px] text-sm whitespace-nowrap'
                    : 'w-fit'
                  }
                `}
                onClick={() => onThreadTypeSelect(threadType.TID)}
              >
                <Plus className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
                {buttonText}
              </Button>
            );
          })}
        </div>
      </div>
    );
  }

  if (filteredThreadTypes.length === 0) {
    return minimalIcon ? (
      <Plus className={`h-6 w-6 text-gray-400 cursor-not-allowed ${className}`} />
    ) : (
      <Button className={`${className} flex items-center`} disabled>
        <Plus className="h-4 w-4" />
        {title}
        <ChevronDown className="h-4 w-4" />
      </Button>
    );
  }

  if (filteredThreadTypes.length === 1) {
    const singleThreadType = filteredThreadTypes[0];

    if (minimalIcon) {
      return (
        <Plus
          className={`h-6 w-6 text-gray-600 hover:text-gray-800 cursor-pointer transition-colors ${className}`}
          onClick={() => onThreadTypeSelect(singleThreadType.TID)}
        />
      );
    }
    return (
      <Button
        className={`${className} flex items-center`}
        onClick={() => onThreadTypeSelect(singleThreadType.TID)}
      >
        <Plus className="h-4 w-4" />
        Enter {singleThreadType.Typ}
      </Button>
    );
  }

  const closeModal = () => {
    setIsOpen(false);
  };

  // Mobile view - use MobileFullScreenDropdown
  if (isMobile) {
    return (
      <MobileFullScreenDropdown
        options={filteredThreadTypes}
        value={null}
        onSelect={(selectedTID, selectedOption) => {
          onThreadTypeSelect(selectedTID);
        }}
        placeholder={`Select ${tskInqOnly ? '' : 'Thread'} Type`}
        displayKey="Typ"
        valueKey="TID"
        className={className}
        label={`${tskInqOnly ? 'Type' : 'Thread Type'}`}
        emptyMessage="No thread types available"
        searchThreshold={999}
        searchPlaceholder="Search thread types..."
        isIcon={minimalIcon}
        showButtonStyle={!minimalIcon}
        buttonTitle={title}
        showClearButton={false}
        showAllOption={false}
        isSorting={false}
        mobileDropdownText={`Select ${tskInqOnly ? '' : 'Thread'} Type`}
        renderOption={(option, isSelected) => (
          <div className="flex items-center gap-3 w-full">
            {option.TID == -1 ? <ListCheck className="text-sahi-dark-blue" /> : <ThreadSahiIcon tID={option.TID} />}
            <span className="font-medium text-gray-900">
              {option.Typ}
            </span>
          </div>
        )}
      />
    );
  }
  // Desktop view - use existing dropdown implementation
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        {minimalIcon ? (
          <div
            className="p-1.5 hover:bg-gray-100 transition-colors text-sahi-primary-color sahi-radius cursor-pointer"
            data-thread-dropdown-trigger
          >
            <Plus className="h-6 w-6" />
          </div>
        ) : (
          <Button
            className={`${className} flex items-center`}
            data-thread-dropdown-trigger
          >
            <Plus className="h-4 w-4" />
            {title}
            <ChevronDown className="h-4 w-4" />
          </Button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="w-50 min-w-0 bg-white border border-gray-200 shadow-lg rounded-md"
        align={dropdownAlign}
      >
        {filteredThreadTypes.map((item) => (
          <DropdownMenuItem
            key={item.TID}
            onClick={() => {
              onThreadTypeSelect(item.TID);
              setIsOpen(false); // Close dropdown after selection
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 focus:bg-gray-50 bg-white border-0"
          >
            {item.TID == -1 ? <ListCheck className="text-sahi-dark-blue" /> : <ThreadSahiIcon tID={item.TID} />}
            <span className="font-medium text-gray-900">{item.Typ}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ThreadDropdown;